import { IS_NATIVE } from "../config.js";
import { loadPreferences } from "./plugins.js";

/**
 * Durability for the keys that hold a user's actual work.
 *
 * WebView `localStorage` is evictable — iOS clears it under storage pressure — so a saved
 * design can simply vanish. `@capacitor/preferences` is backed by UserDefaults /
 * SharedPreferences, which is not.
 *
 * The awkward part is that the readers are synchronous and very early: CameraManager reads
 * `threejs_camera_state` inside its constructor, before StateManager exists, and
 * UserPreferences.read() is sync. Rather than make them async, this hydrates *into*
 * localStorage once at boot — so every existing reader keeps working untouched — and
 * mirrors back out on save.
 */

/** Keys worth surviving an eviction. Dev-only and cosmetic keys are deliberately absent. */
const DURABLE_KEYS = [
  "threejs_user_prefs",
  "threejs_scene_state",
  "threejs_camera_state",
  // The index only: captures are evictable, the designs themselves are not.
  "feydom_design_history",
];

// One blob rather than five entries: a single bridge call at boot and one debounced write,
// instead of five of each.
const BLOB_KEY = "feydom_durable_v1";

let hydrated = false;
let writeTimer = null;
/** Last blob actually written, so an unchanged snapshot costs no bridge call. */
let lastWritten = null;

/**
 * Copy durable state from Preferences into localStorage. Must complete before anything
 * reads — i.e. before the first manager is constructed.
 */
export async function hydrateStorage() {
  if (!IS_NATIVE || hydrated) return;
  hydrated = true;

  try {
    const { Preferences } = await loadPreferences();
    const { value } = await Preferences.get({ key: BLOB_KEY });
    if (!value) {
      // First run after adopting this, or a fresh install. If localStorage already holds
      // state (an upgrade from a build without it), adopt that instead of wiping it.
      persistStorage();
      return;
    }

    const stored = JSON.parse(value);
    for (const key of DURABLE_KEYS) {
      const incoming = stored[key];
      if (incoming === undefined || incoming === null) continue;
      // Only fill gaps: localStorage is the live copy during a session, and a hydrate
      // that clobbered it would undo writes made since the last mirror.
      if (localStorage.getItem(key) === null) localStorage.setItem(key, incoming);
    }
  } catch (error) {
    console.warn("[storage] hydrate failed, using localStorage only:", error);
  } finally {
    installWriteThrough();
  }
}

/**
 * Mirror automatically on any write to a durable key.
 *
 * Asking each writer to call `persistStorage()` after `setItem` was already failing
 * silently — `UserPreferences.save()` writes a durable key and never called it, so its value survived only when some other save
 * happened to fire. A missed mirror is invisible until an eviction throws the work away,
 * which is the worst possible time to find out. DURABLE_KEYS stays the one thing to edit.
 */
function installWriteThrough() {
  const { setItem, removeItem } = Storage.prototype;
  const durable = new Set(DURABLE_KEYS);

  localStorage.setItem = function (key, value) {
    setItem.call(this, key, value);
    if (durable.has(key)) persistStorage();
  };
  localStorage.removeItem = function (key) {
    removeItem.call(this, key);
    if (durable.has(key)) persistStorage();
  };
}

/** Mirror the current durable state out to Preferences. Debounced; safe to call often. */
export function persistStorage() {
  if (!IS_NATIVE) return;
  clearTimeout(writeTimer);
  writeTimer = setTimeout(() => void writeBlob(), 400);
}

async function writeBlob() {
  const snapshot = {};
  for (const key of DURABLE_KEYS) {
    const value = localStorage.getItem(key);
    if (value !== null) snapshot[key] = value;
  }

  // The blob carries the whole scene state, so an orbiting camera would otherwise push
  // tens of KB across the bridge roughly once a second to persist nothing new.
  const serialized = JSON.stringify(snapshot);
  if (serialized === lastWritten) return;

  try {
    const { Preferences } = await loadPreferences();
    await Preferences.set({ key: BLOB_KEY, value: serialized });
    lastWritten = serialized;
  } catch (error) {
    console.warn("[storage] persist failed:", error);
  }
}

/**
 * Force the debounced write to land. Android can kill a backgrounded process before a
 * timer fires, so this runs on the way out.
 */
export async function flushStorage() {
  if (!IS_NATIVE) return;
  clearTimeout(writeTimer);
  await writeBlob();
}
